import "./NewTaskObjectModal.css";

import { OrganisationTypeConstants } from "../../../../../../constants";
import { useAuth } from "../../../../../../useAuth";
import { ActionButtons, Autocomplete, Column, Modal, Row } from "../../../../../../widget";
import { NewTaskObjectModalProps } from "./models";
import { useNewTaskObjectModal } from "./useNewTaskObjectModal";

/**
 * The New-task parent-object chooser (KAN-9565, Figma node 3730-28931) — composed from the existing
 * `Modal`, `Autocomplete` and `ActionButtons` primitives, which is also where the Seed/Hub theming
 * comes from: `Modal` rounds its corners for an Asset Manager on its own.
 *
 * An autocomplete rather than a plain select because the parent universe is organisation-wide: a fixed
 * page would put every object past it out of reach. Each term is searched server-side, and
 * `Autocomplete` debounces the typing before it reaches `onSearch`.
 */
export const NewTaskObjectModal = ({
  show,
  isGeneralTask,
  objects,
  isLoading,
  hasError,
  onSearch,
  onCancel,
  onConfirm,
}: NewTaskObjectModalProps): JSX.Element => {
  const { currentUserType } = useAuth();
  const { objectSelectData, selectedObject, handleSelect, label, helperText, handleSubmit } = useNewTaskObjectModal({
    show,
    isGeneralTask,
    objects,
    onConfirm,
  });

  const corners = currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default";

  return (
    <Modal className="NewTaskObjectModal" show={show} title="New task" onClose={onCancel}>
      <form onSubmit={handleSubmit}>
        <Row spacingV="l">
          <Column span={12} justify="center">
            <p className="body2">{helperText}</p>
          </Column>
        </Row>
        {/* Fields and buttons share a fixed-width centred column, as on the sibling NewTaskModal. */}
        <div className="NewTaskObjectModalFields">
          <Autocomplete
            label={label}
            placeholder="Search"
            data={objectSelectData}
            // Held by the hook, so clearing it on reopen clears the autocomplete's own text too.
            initialKey={selectedObject?.uuid ?? null}
            onTypeSearch={onSearch}
            onSelect={handleSelect}
            disabled={hasError}
            corners={corners}
          />
          <ActionButtons
            fullWidth
            onCancel={onCancel}
            submitButtonLabel="Confirm"
            corners={corners}
            isLoading={isLoading}
            disabled={isLoading || hasError || !selectedObject}
          />
        </div>
      </form>
    </Modal>
  );
};
